export interface S3UploaderConfig {
  apiEndpoint: string; // '/api/s3/presigned'(필수)
  env: "production" | "development"; // 환경 (필수)
  path: string; // 파일 경로 (필수)
  /** 파일명 변환 콜백 - 확장자를 제외한 파일명을 받아 변환합니다 */
  fileNameTransform?: (nameWithoutExt: string, file: File) => string;
  /** true일 경우 파일명 뒤에 UUID를 자동으로 추가합니다 (예: image_abc123.png) */
  appendUUID?: boolean;
  /** false로 설정하면 확장자를 자동으로 붙이지 않음 (기본: true) */
  preserveExtension?: boolean;
  /** 업로드 진행률(0–100) 콜백. S3 PUT 시에만 호출됨 */
  onProgress?: (percent: number) => void;
  /** PUT 요청 타임아웃(ms). 미설정 시 120000(120초). 대용량 비디오에 유리 */
  uploadTimeoutMs?: number;
  /** PUT 실패 시 재시도 횟수. 기본 2(최대 3회 시도) */
  maxRetries?: number;
}

/**
 * 🔒 보안: S3 URL 검증
 * HTTPS 프로토콜 강제 및 URL 형식 검증
 */
function validateS3Url(url: unknown, fieldName: string): string {
  // 타입 검증
  if (typeof url !== "string" || !url || url.trim() === "") {
    throw new Error(
      `${fieldName} is required and must be a non-empty string`
    );
  }

  // HTTPS 프로토콜 강제 (SSRF 방지)
  if (!url.startsWith("https://")) {
    throw new Error(`${fieldName} must use HTTPS protocol`);
  }

  // URL 형식 검증
  try {
    const urlObj = new URL(url);
    // 추가 검증: localhost, private IP 차단
    const hostname = urlObj.hostname.toLowerCase();
    if (
      hostname === "localhost" ||
      hostname.startsWith("127.") ||
      hostname.startsWith("192.168.") ||
      hostname.startsWith("10.") ||
      hostname === "169.254.169.254" // AWS 메타데이터 서버
    ) {
      throw new Error(`${fieldName} cannot point to internal/private networks`);
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes("cannot point to")) {
      throw error;
    }
    throw new Error(`${fieldName} is not a valid URL format`);
  }

  return url;
}

// UUID 생성 함수 (crypto.randomUUID 또는 폴백)
const generateUUID = (): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // 폴백: 간단한 UUID v4 형식 생성
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const createS3Uploader = (config: S3UploaderConfig) => {
  const {
    apiEndpoint,
    env,
    path,
    fileNameTransform,
    appendUUID,
    preserveExtension = true,
    onProgress,
    uploadTimeoutMs = 120000,
    maxRetries = 2,
  } = config;

  // 필수 파라미터 검증
  if (!apiEndpoint || apiEndpoint.trim() === "") {
    throw new Error(
      "apiEndpoint is required for S3 upload. Please provide a valid API endpoint."
    );
  }

  if (!env) {
    throw new Error("env is required. Must be 'development' or 'production'.");
  }

  if (!path || path.trim() === "") {
    throw new Error("path is required and cannot be empty.");
  }

  // 파일명에 UUID 추가하는 함수
  const appendUUIDToFileName = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf(".");
    if (lastDotIndex === -1) {
      // 확장자가 없는 경우
      return `${filename}_${generateUUID()}`;
    }
    const name = filename.substring(0, lastDotIndex);
    const ext = filename.substring(lastDotIndex);
    return `${name}_${generateUUID()}${ext}`;
  };

  // 계층 구조 파일명 생성 함수
  const generateHierarchicalFileName = (file: File): string => {
    // 0. 확장자 분리
    const originalName = file.name;
    const lastDotIndex = originalName.lastIndexOf(".");
    const nameWithoutExt =
      lastDotIndex === -1
        ? originalName
        : originalName.substring(0, lastDotIndex);
    const extension =
      lastDotIndex === -1 ? "" : originalName.substring(lastDotIndex);

    let filename = nameWithoutExt;

    // 1. 사용자 정의 파일명 변환 콜백 적용 (확장자 제외한 이름만)
    if (fileNameTransform) {
      filename = fileNameTransform(filename, file);
    }

    // 2. UUID 자동 추가 (appendUUID가 true인 경우)
    if (appendUUID) {
      filename = `${filename}_${generateUUID()}`;
    }

    // 3. 확장자 다시 붙이기 (preserveExtension이 true인 경우만)
    if (preserveExtension) {
      filename = `${filename}${extension}`;
    }

    // {env}/{path}/{filename}
    return `${env}/${path}/${filename}`;
  };

  const debugLog = (loc: string, msg: string, data: Record<string, unknown>) => {
    const p = fetch("http://127.0.0.1:7686/ingest/1f8ee1c5-0cf0-4ae7-91ed-5ea7ed17130a", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Debug-Session-Id": "b73262" },
      body: JSON.stringify({ sessionId: "b73262", location: loc, message: msg, data, timestamp: Date.now() }),
    });
    if (p && typeof (p as Promise<unknown>).catch === "function") (p as Promise<unknown>).catch(() => {});
  };

  return async (file: File): Promise<string> => {
    try {
      if (!apiEndpoint || apiEndpoint.trim() === "") {
        throw new Error(
          "Invalid apiEndpoint: Cannot upload file without a valid API ENDPOINT"
        );
      }

      const fileName = generateHierarchicalFileName(file);
      const contentType = file.type || "application/octet-stream";
      const presignedUrlFull = `${apiEndpoint}?key=${encodeURIComponent(fileName)}&contentType=${encodeURIComponent(contentType)}`;
      const tPresigned = Date.now();
      // #region agent log
      debugLog("s3:step1:presignedReq", "fetching presigned URL", {
        fileName,
        contentType,
        apiEndpoint,
      });
      // #endregion
      const response = await fetch(presignedUrlFull);

      // #region agent log
      debugLog("s3:step2:presignedRes", "presigned response", {
        ok: response.ok,
        status: response.status,
        elapsedMs: Date.now() - tPresigned,
      });
      // #endregion
      if (!response.ok) {
        const errorText = (await response.text()) || "";
        debugLog("s3:step2b:presignedErr", "presigned failed", {
          status: response.status,
          errorText: errorText.slice(0, 200),
        });
        throw new Error(
          `Failed to get presigned URL: ${response.statusText}, ${errorText}`
        );
      }

      const responseData = await response.json();
      const { presignedUrl, publicUrl } = responseData;
      const validatedPresignedUrl = validateS3Url(presignedUrl, "presignedUrl");
      const validatedPublicUrl = validateS3Url(publicUrl, "publicUrl");

      const tPut = Date.now();
      // #region agent log
      debugLog("s3:step3:putReq", "S3 PUT request", { publicUrlLen: validatedPublicUrl?.length });
      // #endregion

      let lastError: Error | undefined;
      const attempts = maxRetries + 1;
      for (let attempt = 0; attempt < attempts; attempt++) {
        try {
          if (onProgress && typeof XMLHttpRequest !== "undefined") {
            await new Promise<void>((resolve, reject) => {
              const xhr = new XMLHttpRequest();
              xhr.timeout = uploadTimeoutMs;

              // --- 진행률 보간용 상태 (progress 이벤트가 드물 때 중간 % 표시) ---
              let lastReported = -1; // 마지막으로 콜백에 넘긴 퍼센트 (중복 호출 방지)
              const REPORT_INTERVAL_MS = 100; // 보간 타이머 주기(ms)
              const simulatedCap = 90; // 보간 진행률 상한(%). 90 이상은 실제 progress 값 사용
              let simulatedPercent = 0;

              // 보간 타이머: progress 이벤트가 잘 오지 않을 때 주기적으로 진행률 상승
              const simId = setInterval(() => {
                if (simulatedPercent >= simulatedCap) return;
                simulatedPercent = Math.min(simulatedCap, simulatedPercent + 3);
                const p = Math.min(100, simulatedPercent);
                if (p > lastReported) {
                  lastReported = p;
                  onProgress(p);
                }
              }, REPORT_INTERVAL_MS);
              const clearSim = () => {
                clearInterval(simId);
              };

              // XHR 실제 업로드 진행률 (loaded/total)
              xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                  const p = Math.min(100, Math.round((e.loaded / e.total) * 100));
                  if (p >= simulatedCap) clearSim(); // 실제 진행이 90% 넘으면 보간 타이머 중단
                  const toReport = Math.max(p, simulatedPercent); // 실제 vs 보간 중 큰 값 사용
                  if (toReport > lastReported) {
                    lastReported = toReport;
                    onProgress(toReport);
                  }
                }
              };
              xhr.onload = () => {
                clearSim();
                if (xhr.status >= 200 && xhr.status < 300) {
                  if (lastReported < 100) onProgress(100); // 완료 시 100% 한 번 더 보장
                  resolve();
                } else {
                  reject(new Error(`Failed to upload file: ${xhr.statusText}`));
                }
              };
              xhr.onerror = () => {
                clearSim();
                reject(new Error("Upload failed"));
              };
              xhr.ontimeout = () => {
                clearSim();
                reject(new Error("Upload timeout"));
              };
              xhr.open("PUT", validatedPresignedUrl);
              xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
              onProgress(0); // 업로드 시작 직후 0% 알림
              lastReported = 0;
              xhr.send(file);
            });
          } else {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), uploadTimeoutMs);
            const uploadResponse = await fetch(validatedPresignedUrl, {
              method: "PUT",
              headers: {
                "Content-Type": file.type || "application/octet-stream",
              },
              body: file,
              signal: controller.signal,
            });
            clearTimeout(timeoutId);

            // #region agent log
            debugLog("s3:step4:putRes", "S3 PUT response", {
              ok: uploadResponse.ok,
              status: uploadResponse.status,
              putElapsedMs: Date.now() - tPut,
            });
            // #endregion
            if (!uploadResponse.ok) {
              throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);
            }
          }
          debugLog("s3:step5:return", "returning publicUrl", { urlPrefix: validatedPublicUrl.slice(0, 80) });
          return validatedPublicUrl;
        } catch (err) {
          lastError = err instanceof Error ? err : new Error(String(err));
          if (attempt < attempts - 1) {
            debugLog("s3:putRetry", "PUT failed, retrying", { attempt: attempt + 1, attempts });
          } else {
            throw lastError;
          }
        }
      }

      throw lastError ?? new Error("Upload failed");
    } catch (error) {
      console.error("S3 upload failed:", error);
      throw error;
    }
  };
};
